import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { authAPI } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, ArrowLeft, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function RegistrationVerifyPage() {
  const { verifyRegistration } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const inputRefs = useRef([]);

  // Redirect if no email
  useEffect(() => {
    if (!email) navigate('/register', { replace: true });
  }, [email, navigate]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    setError('');

    // Auto-advance to next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (digit && index === 5 && newCode.every(d => d)) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const newCode = [...code];
    for (let i = 0; i < 6; i++) {
      newCode[i] = pasted[i] || '';
    }
    setCode(newCode);
    // Focus last filled or next empty
    const nextIndex = Math.min(pasted.length, 5);
    inputRefs.current[nextIndex]?.focus();
    // Auto-submit if all 6
    if (pasted.length === 6) {
      handleSubmit(pasted);
    }
  };

  const handleSubmit = async (codeStr) => {
    const finalCode = codeStr || code.join('');
    if (finalCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }
    setLoading(true);
    setError('');
    const result = await verifyRegistration(email, finalCode);
    if (result.success) {
      toast.success('Email verified! Welcome to Pulse.');
      navigate('/admin-onboarding');
    } else {
      setError(result.error);
      // Clear code on failure
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
    setLoading(false);
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setResending(true);
    try {
      await authAPI.resendVerification({ email });
      toast.success('A new verification code has been sent.');
      setCooldown(60);
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      toast.error('Failed to resend code. Please try again.');
    }
    setResending(false);
  };

  const maskedEmail = email
    ? email.replace(/^(.{2})(.*)(@.*)$/, (_, a, b, c) => a + '*'.repeat(b.length) + c)
    : '';

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 px-6">
      <div className="mx-auto w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Link to="/">
            <img src="/pulse-logo-with-name.png" alt="Pulse by ProjectAI" className="h-24 object-contain" />
          </Link>
        </div>

        <Card className="shadow-lg border-slate-200" data-testid="verify-account-card">
          <CardHeader className="text-center space-y-3">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#5AA2AE]/10 flex items-center justify-center">
              <Mail className="h-7 w-7 text-[#5AA2AE]" />
            </div>
            <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Check your email
            </CardTitle>
            <CardDescription className="text-sm">
              We sent a 6-digit verification code to<br />
              <span className="font-medium text-slate-700" data-testid="verify-masked-email">{maskedEmail}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {error && (
              <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm text-center" data-testid="verify-error">
                {error}
              </div>
            )}

            {/* 6-digit code input */}
            <div className="flex justify-center gap-2" onPaste={handlePaste}>
              {code.map((digit, i) => (
                <Input
                  key={i}
                  ref={el => inputRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  autoComplete={i === 0 ? 'one-time-code' : 'off'}
                  maxLength={1}
                  value={digit}
                  onChange={e => handleChange(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  className="w-12 h-14 text-center text-xl font-semibold border-slate-300 focus:border-[#5AA2AE] focus:ring-[#5AA2AE]"
                  disabled={loading}
                  data-testid={`verify-code-input-${i}`}
                />
              ))}
            </div>

            <Button
              className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              disabled={loading || code.some(d => !d)}
              onClick={() => handleSubmit()}
              data-testid="verify-submit-btn"
            >
              {loading ? 'Verifying...' : 'Verify Email'}
            </Button>

            {/* Resend */}
            <div className="text-center space-y-2">
              <p className="text-sm text-slate-500">Didn't receive the code?</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResend}
                disabled={resending || cooldown > 0}
                className="text-[#5AA2AE] hover:text-[#5AA2AE]/80"
                data-testid="verify-resend-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${resending ? 'animate-spin' : ''}`} />
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
              </Button>
            </div>

            {/* Back to register */}
            <div className="text-center pt-2 border-t border-slate-100">
              <Link
                to="/register"
                className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
                data-testid="verify-back-link"
              >
                <ArrowLeft className="h-3 w-3" />
                Back to registration
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
